import { useEffect, StrictMode, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth, SESSION_TIMEOUT } from "@/providers/authProvider";
import { clearAuthStorage } from "@/lib/session";
import { refreshWithRetry } from "@/lib/authClient";
import api from "@/api/apiClient";
import './index.css'

// Import the generated route tree
import { routeTree } from "./routeTree.gen";
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

// Create a new router instance
const RETRY_COUNT = 3
export const queryClient = new QueryClient(
  {
    defaultOptions: {
      queries: {
        throwOnError: (error, query): boolean => {
          if(query.state.data) return false
          if(axios.isAxiosError(error) && error.response?.status === 404) return false
          return true
        },
        retry: (failureCount, error): boolean => {
          if (failureCount === RETRY_COUNT) return false
          if (
            axios.isAxiosError(error) &&
            error.response?.status &&
            error.response.status < 500
          ) return false
          return true
        },
        refetchOnWindowFocus: false,
        staleTime: 120000
      }
    }
  }
);
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: { auth: undefined!, queryClient },
});

// Set interceptors of Axios
api.interceptors.request.use(
  (config) => {
    const idToken = localStorage.getItem("idToken");
    if (idToken != null && config && config.headers) {
      config.headers.Authorization = `Bearer ${idToken}`;
      return config;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const isNetworkError = error.message && error.message == "Network Error";
    if (!isNetworkError) {
      const status: number = error.response ? error.response.status : 500;
      const originalRequest = error.config as
        | (InternalAxiosRequestConfig & { _retry?: boolean })
        | undefined;
      // 401 時の方針:
      //  - originalRequest が取れて未リトライなら 1 回だけ refresh + 元リクエスト再実行を試みる。
      //  - それ以外（refresh 失敗 / リトライ後の再 401 / そもそも config が取れない 401）は
      //    どのケースでも認証情報を破棄して signin へ遷移し、中途半端な認証状態を残さない。
      if (status == 401) {
        if (originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;
          const refreshed = await refreshWithRetry();
          if (refreshed) {
            const newToken = localStorage.getItem("idToken");
            if (newToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return api(originalRequest);
          }
        }
        clearAuthStorage();
        queryClient.clear();
        router.navigate({ to: "/signin" });
      }
    }
    return Promise.reject(error);
  }
);
// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// eslint-disable-next-line react-refresh/only-export-components
const InnerApp = () => {
  const auth = useAuth();
  const navigate = router.navigate;

  // ユーザー操作時に最終アクティブ時間を更新する。
  // リスナーは常に登録し、ログイン後（再マウントを待たず）にも有効化されるようにする。
  // タイムアウト判定とリダイレクトは下のインターバルに集約する。
  useEffect(() => {
    const resetEvents = [
      "mousemove", "mouseup", "mousedown", "click", "wheel",
      "keydown", "keyup",
    ];
    const updateLastActive = () => {
      // セッションがある場合のみ lastActive を更新する
      if (localStorage.getItem("idToken")) {
        localStorage.setItem("lastActive", Date.now().toString());
      }
    };
    for (const resetEvent of resetEvents) {
      window.addEventListener(resetEvent, updateLastActive);
    }
    return () => {
      for (const resetEvent of resetEvents) {
        window.removeEventListener(resetEvent, updateLastActive);
      }
    };
  }, []);

  // 120分無操作タイムアウト: 期限切れを検知したら全認証情報を破棄して signin へ
  useEffect(() => {
    const interval = setInterval(() => {
      const lastActive = localStorage.getItem("lastActive");
      const timedOut =
        !lastActive || Date.now() - Number(lastActive) > SESSION_TIMEOUT;
      // 既存セッションがある場合のみリダイレクト（signin 画面でのループを防ぐ）
      if (timedOut && localStorage.getItem("idToken")) {
        clearAuthStorage();
        navigate({ to: "/signin" });
      }
    }, 30000); // 30秒ごとにチェック（120分タイムアウトに対し十分な精度で、負荷を抑える）

    return () => clearInterval(interval);
  }, [navigate]);

  return <RouterProvider router={router} context={{ auth }} />;
};

// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <Suspense fallback="...loading">
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <InnerApp />
          </AuthProvider>
        </QueryClientProvider>
      </Suspense>
    </StrictMode>
  );
}
